import type { MessageConfirmation, MessagePublic, MessageReadEvent, MessageSend } from '@repo/types/message';
import type { UserPublic } from '@repo/types/users';
import type { Socket } from 'socket.io-client';

import { io } from 'socket.io-client';

import { useChatStore } from '@/store/chat-store';

export const MESSAGES_PAGE_SIZE = 20;
export const MESSAGE_CONFIRMATION_TIMEOUT = 10000;

class ChatSocketService {
  private socket: Socket | null = null;
  private static instance: ChatSocketService | null = null;
  private disconnectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly DISCONNECTION_TIMEOUT = 5000;
  private cachedUsers: UserPublic[] = [];

  // Private constructor prevents new instances
  private constructor() {}

  // Static method to get the instance
  public static getInstance(): ChatSocketService {
    if (!ChatSocketService.instance) {
      ChatSocketService.instance = new ChatSocketService();
    }
    return ChatSocketService.instance;
  }

  connect(chatId: number) {
    this.disconnect();
    const store = useChatStore.getState();
    store.resetToDefaults();
    store.setActiveChat(chatId);
    store.setConnectionStatus('connecting');

    this.socket = this.socketIOClient(chatId);
    // Set up event listeners
    this.socket.io.on('error', this.onErrorEvent);
    this.socket.on('read', this.onReadReceipt);
    this.socket.on('connect', this.onConnect);
    this.socket.on('disconnect', this.onDisconnect);
    this.socket.on('message', this.onMessageEvent);
    this.socket.on('msgDelete', this.onMessageDelete);
    this.socket.on('confirm', this.onMessageConfirmationEvent);
    this.socket.on('reload', this.onReloadEvent);
    this.socket.on('getHistory', this.onGetHistoryEvent);
    this.socket.on('getUsers', this.onGetUsersEvent);

    // Connect to socket server
    this.socket.connect();
  }

  private socketIOClient(id: number): Socket {
    return io(import.meta.env.VITE_SOCKET_DOMAIN, {
      withCredentials: true,
      autoConnect: false,
      query: { chatId: id },
      path: import.meta.env.VITE_SOCKET_IO,
      reconnectionAttempts: 6,
      timeout: 100000,
    });
  }

  disconnect(): void {
    if (this.disconnectionTimeout) {
      clearTimeout(this.disconnectionTimeout);
      this.disconnectionTimeout = null;
    }

    if (this.socket) {
      useChatStore.getState().setConnectionStatus('disconnected');
      this.socket.disconnect();
    }
  }

  reconnect(chatId: number): void {
    if (this.socket) {
      useChatStore.getState().setConnectionStatus('connecting');
      this.connect(chatId);
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  requestUsers(): void {
    if (!this.socket) return;
    this.socket.emit('getUsers');
  }

  sendMessage(message: MessageSend): string | null {
    if (!this.socket) {
      return null;
    }
    const store = useChatStore.getState();
    if (!store.chatId) return null;

    store.appendMessages([message]);
    store.setReplyingTo(null);
    this.socket.emit('message', { ...message });

    if (message.tempUUID) {
      store.updateMessageStatus(message.tempUUID, 'pending');
      store.setMessageConfirmationTimeout(message.tempUUID);
    }

    return message.tempUUID ?? null;
  }

  retryMessage(tempUUID?: string): void {
    const store = useChatStore.getState();
    const message = store.messages.find((msg) => msg.tempUUID === tempUUID);

    if (message && this.socket && tempUUID) {
      // Update message status to sending
      store.updateMessageStatus(tempUUID, 'pending');

      // Resend the message
      this.socket.emit('message', { ...message });

      // Set a new confirmation timeout
      store.setMessageConfirmationTimeout(tempUUID);
    }
  }

  deleteMessage(id: number): void {
    if (!this.socket) return;
    const store = useChatStore.getState();
    if (!store.chatId) return;
    this.socket.emit('msgDelete', { id });
  }

  requestHistory(): void {
    if (!this.socket) return;
    const store = useChatStore.getState();
    const { chatId, hasMore, loading, connectionStatus, last } = store;

    if (chatId && hasMore && !loading && connectionStatus === 'connected') {
      store.setLoading(true);
      this.socket.emit('getHistory', {
        limit: MESSAGES_PAGE_SIZE,
        last: last,
      });
    }
  }

  markMessageAsRead(id: number) {
    if (!this.socket) return;
    this.socket.emit('read', { id });
  }

  private reloadMessages(): void {
    if (!this.socket) return;
    const store = useChatStore.getState();
    if (!store.chatId) return;

    const newestMessageId = [...store.messages].reverse().find((msg) => msg.id)?.id;
    this.socket.emit('reload', { newest: newestMessageId });
  }

  getUsers(): UserPublic[] {
    return this.cachedUsers;
  }

  // Event handlers
  private onErrorEvent = (): void => {
    const store = useChatStore.getState();
    store.setConnectionStatus('disconnected');
  };

  private onConnect = () => {
    const store = useChatStore.getState();
    store.setConnectionStatus('connected');

    if (this.disconnectionTimeout) {
      clearTimeout(this.disconnectionTimeout);
      this.disconnectionTimeout = null;
    }

    if (store.messages.length > 0) {
      this.reloadMessages();
    }

    store.setIsDisconnected(false);
    this.requestUsers();
  };

  private onDisconnect = (): void => {
    const store = useChatStore.getState();
    store.setConnectionStatus('disconnected');

    if (this.disconnectionTimeout) {
      clearTimeout(this.disconnectionTimeout);
    }

    this.disconnectionTimeout = setTimeout(() => {
      const store = useChatStore.getState();
      if (store.connectionStatus === 'disconnected') {
        store.setIsDisconnected(true);
      }
    }, this.DISCONNECTION_TIMEOUT);
  };

  private onMessageEvent = (message: MessageSend): void => {
    const store = useChatStore.getState();

    if (!message.id) return;

    store.appendMessages([message]);
  };

  private onMessageConfirmationEvent = (data: MessageConfirmation): void => {
    const store = useChatStore.getState();
    store.confirmMessage(data);
  };

  private onMessageDelete = (data: { id: number; content: string }): void => {
    useChatStore.getState().deleteMessage(data.id, data.content);
  };

  private onReloadEvent = (messages: MessagePublic[]): void => {
    const store = useChatStore.getState();
    if (!messages.length) return;

    store.appendMessages(messages);
  };

  private onGetHistoryEvent = (messages: MessagePublic[]): void => {
    const store = useChatStore.getState();
    const latestMessageId = messages[0]?.id;
    store.setLoading(false);

    if (messages.length < MESSAGES_PAGE_SIZE) {
      store.setHasMore(false);
    }

    if (latestMessageId) {
      store.setLast(latestMessageId);
      store.prependMessages(messages);
    }
  };

  private onGetUsersEvent = (users: UserPublic[]): void => {
    this.cachedUsers = users;
  };

  private onReadReceipt = (data: MessageReadEvent): void => {
    // The server carries the reader on the event; the cached list is only a
    // fallback, since it omits members attached to the chat via a group.
    const user = data.user ?? this.cachedUsers.find((u) => u.id === data.userId);

    if (user) {
      useChatStore.getState().addReadReceipt(data.id, user, data.createdAt);
    }
  };
}

export const chatSocketService = ChatSocketService.getInstance();
